import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Chip,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Button,
  IconButton, // ← added
  Snackbar, // ← added
  Alert, // ← added
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import Loader from "../../../components/loader/Loader";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Payment } from "@mui/icons-material";
import MicIcon from "@mui/icons-material/Mic"; // ← added
import MicOffIcon from "@mui/icons-material/MicOff"; // ← added
import ExcelJS from "exceljs";
import DownloadIcon from "@mui/icons-material/Download";
import { buildWorkbookWithPivotSafe } from "../../../hooks/Pivottablebuilder";

// ─── Voice Recognition Hook ───────────────────────────────────────────────────
function useVoiceRecognition({ onResult, onError }) {
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);

  // Check lazily at call time — avoids false negatives during SSR / early render
  const getSR = () =>
    (typeof window !== "undefined" &&
      (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
    null;

  const isSupported = !!getSR();

  const start = () => {
    const SR = getSR();
    if (!SR) {
      onError?.(
        "Voice recognition is not supported in this browser. Please use Chrome or Edge.",
      );
      return;
    }
    const recognition = new SR();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = (e) => {
      setListening(false);
      onError?.(`Voice error: ${e.error}`);
    };
    recognition.onresult = (e) =>
      onResult?.(e.results[0][0].transcript.toLowerCase().trim());
    recognitionRef.current = recognition;
    recognition.start();
  };

  const stop = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  return { listening, start, stop, isSupported };
}
console.log(window.SpeechRecognition);
console.log(window.webkitSpeechRecognition);
console.log("Browser:", navigator.userAgent);
console.log("SpeechRecognition:", window.SpeechRecognition);
console.log("webkitSpeechRecognition:", window.webkitSpeechRecognition);
// ─── Voice Command Parser ─────────────────────────────────────────────────────
// regions/shops are passed in so voice can match spoken region/shop names against
// the actual lists loaded from redux (case-insensitive substring match).
function parseVoiceCommand(transcript, regions = [], shops = []) {
  const changes = {};

  // Delivery type
  if (/pick\s*up/.test(transcript)) changes.orderMethod = "Pick Up";
  else if (/door\s*step|doorstep|home\s*delivery/.test(transcript))
    changes.orderMethod = "Door Step Delivery";
  else if (/all\s*deliver(y|ies)?|all\s*type/.test(transcript))
    changes.orderMethod = "ALL";

  // Platform
  if (/platform\s*(android|play\s*store)/.test(transcript))
    changes.platform = "android";
  else if (/platform\s*(ios|iphone|apple)/.test(transcript))
    changes.platform = "ios";
  else if (/platform\s*(web|browser|website)/.test(transcript))
    changes.platform = "web";
  else if (/all\s*platform/.test(transcript)) changes.platform = "ALL";

  // ── Date helpers ──────────────────────────────────────────────────────────
  const monthMap = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
    jan: "01",
    feb: "02",
    mar: "03",
    apr: "04",
    jun: "06",
    jul: "07",
    aug: "08",
    sep: "09",
    oct: "10",
    nov: "11",
    dec: "12",
  };
  const monthNames = Object.keys(monthMap);

  const pad = (n) => String(n).padStart(2, "0");
  const toISO = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const lastDayOfMonth = (year, monthIndex0) =>
    new Date(year, monthIndex0 + 1, 0).getDate();

  const parseDate = (str) => {
    if (!str) return null;
    str = str.trim();
    if (str === "today") return toISO(new Date());
    if (str === "yesterday") {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return toISO(d);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const m = str.match(
      /(\d{1,2})\s+([a-z]+)\s+(\d{4})|([a-z]+)\s+(\d{1,2})\s+(\d{4})/,
    );
    if (m) {
      let day, month, year;
      if (m[1]) {
        day = m[1].padStart(2, "0");
        month = monthMap[m[2]];
        year = m[3];
      } else {
        month = monthMap[m[4]];
        day = m[5].padStart(2, "0");
        year = m[6];
      }
      if (month && day && year) return `${year}-${month}-${day}`;
    }
    return null;
  };

  // Returns {from, to} for a calendar month given a month name and optional year
  const monthRange = (monthName, yearStr) => {
    const monthIdx = parseInt(monthMap[monthName], 10) - 1; // 0-based
    const year = yearStr ? parseInt(yearStr, 10) : new Date().getFullYear();
    const from = `${year}-${pad(monthIdx + 1)}-01`;
    const to = `${year}-${pad(monthIdx + 1)}-${pad(lastDayOfMonth(year, monthIdx))}`;
    return { from, to };
  };

  // Returns {from, to} for the current calendar week (Sun–Sat) or previous one
  const weekRange = (offsetWeeks = 0) => {
    const now = new Date();
    now.setDate(now.getDate() + offsetWeeks * 7);
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    return { from: toISO(sunday), to: toISO(saturday) };
  };

  let dateHandled = false;

  // ── "this/last month" or "this/last week" ──────────────────────────────────
  if (/this\s+month/.test(transcript)) {
    const now = new Date();
    const from = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
    const to = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(lastDayOfMonth(now.getFullYear(), now.getMonth()))}`;
    changes.fromDate = from;
    changes.toDate = to;
    dateHandled = true;
  } else if (/last\s+month/.test(transcript)) {
    const now = new Date();
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const y = lastMonthDate.getFullYear();
    const mIdx = lastMonthDate.getMonth();
    changes.fromDate = `${y}-${pad(mIdx + 1)}-01`;
    changes.toDate = `${y}-${pad(mIdx + 1)}-${pad(lastDayOfMonth(y, mIdx))}`;
    dateHandled = true;
  } else if (/this\s+week/.test(transcript)) {
    const r = weekRange(0);
    changes.fromDate = r.from;
    changes.toDate = r.to;
    dateHandled = true;
  } else if (/last\s+week/.test(transcript)) {
    const r = weekRange(-1);
    changes.fromDate = r.from;
    changes.toDate = r.to;
    dateHandled = true;
  } else if (
    /\btoday\b/.test(transcript) &&
    !/from|to\s+date/.test(transcript)
  ) {
    const iso = toISO(new Date());
    changes.fromDate = iso;
    changes.toDate = iso;
    dateHandled = true;
  } else if (
    /\byesterday\b/.test(transcript) &&
    !/from|to\s+date/.test(transcript)
  ) {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const iso = toISO(d);
    changes.fromDate = iso;
    changes.toDate = iso;
    dateHandled = true;
  }

  // ── Bare month name, e.g. "march report", "show me march", "june sales" ───
  // Only fires if "this/last month" etc. above didn't already set dates, and
  // only if explicit from/to phrasing isn't present (handled below instead).
  if (!dateHandled && !/\bfrom\b/.test(transcript)) {
    const monthPattern = new RegExp(
      `\\b(${monthNames.join("|")})\\b(?:\\s+(\\d{4}))?`,
      "i",
    );
    const monthMatch = transcript.match(monthPattern);
    if (monthMatch) {
      const { from, to } = monthRange(
        monthMatch[1].toLowerCase(),
        monthMatch[2],
      );
      changes.fromDate = from;
      changes.toDate = to;
      dateHandled = true;
    }
  }

  // ── Explicit "from ... to ..." date phrasing (existing behavior, takes
  // priority over the bare-month shortcut above since it's more specific) ──
  const fromMatch = transcript.match(/from\s+(?:date\s+)?(.+?)(?:\s+to\s+|$)/);
  if (fromMatch) {
    const p = parseDate(fromMatch[1]);
    if (p) {
      changes.fromDate = p;
      dateHandled = true;
    }
  }
  const toMatch = transcript.match(/to\s+(?:date\s+)?(.+?)(?:\s+from\s+|$)/);
  if (toMatch) {
    const p = parseDate(toMatch[1]);
    if (p) {
      changes.toDate = p;
      dateHandled = true;
    }
  }

  // ── Region match — "region chennai", "in mumbai" ────────────────────────
  const regionMatch = transcript.match(
    /region\s+([a-z\s]+?)(?:\s+shop\s|\s+platform\s|$)/,
  );
  if (regionMatch) {
    const spoken = regionMatch[1].trim();
    if (/^all$/.test(spoken)) {
      changes.region = "";
    } else {
      const found = regions.find((r) =>
        (r.city?.name || "").toLowerCase().includes(spoken),
      );
      if (found) changes.region = found.region_id;
    }
  }

  // ── Shop match — "shop downtown", "shop Uthangudi" ─────────────────────
  const shopMatch = transcript.match(
    /shop\s+([a-z0-9\s]+?)(?:\s+region\s|\s+platform\s|$)/,
  );
  if (shopMatch) {
    const spoken = shopMatch[1].trim();
    if (/^all$/.test(spoken)) {
      changes.shop = "";
    } else {
      const found = shops.find(
        (s) =>
          (s.shop_name || "").toLowerCase().includes(spoken) ||
          (s.shop_code || "").toLowerCase().includes(spoken),
      );
      if (found) changes.shop = found.shop_details_id;
    }
  }

  // Clear
  if (/clear|reset/.test(transcript)) changes.clear = true;

  return changes;
}

function SalesReport({ type }) {
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryData, setSummaryData] = useState(null);

  const loginType = sessionStorage.getItem("loginType") || "admin";
  const loggedInShopId = Number(sessionStorage.getItem("shop_id")) || 0;

  const [orderMethod, setOrderMethod] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [region, setRegion] = useState("");
  const [shop, setShop] = useState("");
  const [platform, setPlatform] = useState("ALL");

  // ── Voice state ──────────────────────────────────────────────────────────────
  const [voiceStatus, setVoiceStatus] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const regions = useSelector((state) => state.counter.allRegionList);
  const allShops = useSelector((state) => state.counter.shopList);

  const { listening, start, stop, isSupported } = useVoiceRecognition({
    onResult: (transcript) => {
      setVoiceStatus(`"${transcript}"`);
      const changes = parseVoiceCommand(transcript, regions, allShops);
      if (Object.keys(changes).length === 0) {
        setSnackbar({
          open: true,
          message: `Heard: "${transcript}" — no filter matched.`,
          severity: "warning",
        });
        return;
      }
      if (changes.clear) {
        setOrderMethod("ALL");
        setFromDate("");
        setToDate("");
        setRegion("");
        setShop("");
        setPlatform("ALL");
        setVoiceStatus("Filters cleared");
        setSnackbar({
          open: true,
          message: "Filters cleared via voice.",
          severity: "success",
        });
        return;
      }
      if (changes.orderMethod !== undefined)
        setOrderMethod(changes.orderMethod);
      if (changes.fromDate !== undefined) setFromDate(changes.fromDate);
      if (changes.toDate !== undefined) setToDate(changes.toDate);
      if (changes.platform !== undefined) setPlatform(changes.platform);
      if (changes.region !== undefined) {
        setRegion(changes.region);
        if (loginType !== "shop" && changes.shop === undefined) setShop("");
      }
      if (changes.shop !== undefined) setShop(changes.shop);

      const appliedKeys = Object.keys(changes).filter((k) => k !== "clear");
      const notFoundBits = [];
      if (/region\s+/.test(transcript) && changes.region === undefined)
        notFoundBits.push("region not recognized");
      if (/shop\s+/.test(transcript) && changes.shop === undefined)
        notFoundBits.push("shop not recognized");

      const message = appliedKeys.length
        ? `Applied: ${appliedKeys.join(", ")}${notFoundBits.length ? ` (${notFoundBits.join(", ")})` : ""}`
        : `Heard: "${transcript}" — ${notFoundBits.join(", ") || "no filter matched"}.`;
      setSnackbar({
        open: true,
        message,
        severity: appliedKeys.length ? "success" : "warning",
      });
    },
    onError: (msg) =>
      setSnackbar({ open: true, message: msg, severity: "error" }),
  });

  const handleMicClick = () => {
    if (listening) stop();
    else {
      setVoiceStatus("");
      start();
    }
  };
  // ── End voice state ──────────────────────────────────────────────────────────

  const [filteredShops, setFilteredShops] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);

  const orderMethods = ["ALL", "Door Step Delivery", "Pick Up"];

  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      setShop(loggedInShopId);
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      if (shopDetails && shopDetails.region_id) {
        setRegion(shopDetails.region_id);
      }
    }
  }, [loginType, loggedInShopId, allShops]);

  const loadOrders = useCallback(() => {
    setLoader(true);
    const params = {};
    if (orderMethod !== "ALL") params.delivery_mode = orderMethod;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (region) params.region_id = parseInt(region);
    if (shop) params.shop_id = parseInt(shop);
    if (platform !== "ALL") params.platform = platform;

    axios
      .post("/reports/sales", params)
      .then((res) => {
        const orders = res?.data?.data?.data || [];
        const summary = res?.data?.data?.summary || null;

        const transformedOrders = [...orders].reverse().map((order) => {
          const products = (order.ordered_products || []).map((product) => {
            const details = Array.isArray(product.product_details)
              ? product.product_details[0]
              : product.product_details;

            const weightObj = product.variation?.weight?.[0] || null;

            const weightName = weightObj?.weight_name || "";
            const unitName = weightObj?.unit?.unit_name || "";
            const variant = weightName
              ? `${weightName} ${unitName}`.trim()
              : "";
            const productName = details?.product_name || "";
            const sku = product.variation?.sku || "";

            return {
              product_name: productName,
              productname_sku: sku ? `${productName}_${sku}` : productName,
              variant: variant,
              cat_code_id:
                details?.category_code?.category_code ||
                details?.cat_code_id ||
                "",
              product_qty: Number(product.product_quantity || 0),
              product_total: Number(product.product_total || 0),
            };
          });

          const totalProductPrice = products.reduce(
            (s, p) => s + p.product_total,
            0,
          );
          const totalProductQty = products.reduce(
            (s, p) => s + p.product_qty,
            0,
          );
          const allAddOnNames = (order.ordered_addons || [])
            .map((addon) => addon.product_name)
            .filter(Boolean)
            .join(", ");
          const totalAddOnQty = (order.ordered_addons || []).reduce(
            (sum, addon) => sum + Number(addon.quantity || 0),
            0,
          );
          const totalAddOnPrice = (order.ordered_addons || []).reduce(
            (sum, addon) => sum + Number(addon.price || 0),
            0,
          );

          return {
            ...order,
            shop_name:
              order.delivery_mode === "Door Step Delivery"
                ? getShopName(order.shop_orders?.shop_id)
                : getShopName(order.shop_id),
            region_name:
              order.delivery_mode === "Door Step Delivery"
                ? getRegionName(order.shop_orders?.region_id)
                : getRegionName(order.region_id),
            qty: totalProductQty + totalAddOnQty,
            products_list: products,
            products_names: products.map((p) => p.product_name).join(", "),
            product_total: totalProductPrice,
            products_qty: totalProductQty,
            addons_names: allAddOnNames || "",
            addons_qty: totalAddOnQty || 0,
            addons_total: totalAddOnPrice,
          };
        });

        setFormData(transformedOrders);
        setSummaryData(summary);
        setTotalCount(orders.length);
      })
      .catch(() => {
        setFormData([]);
        setSummaryData(null);
        setTotalCount(0);
      })
      .finally(() => setLoader(false));
  }, [orderMethod, fromDate, toDate, region, shop, platform]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      if (shopDetails && shopDetails.region_id) {
        const shopRegion = regions.filter(
          (r) => r.region_id === shopDetails.region_id,
        );
        setFilteredRegions(shopRegion);
      } else {
        setFilteredRegions([]);
      }
    } else {
      setFilteredRegions(regions);
    }
  }, [loginType, loggedInShopId, regions, allShops]);

  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      setFilteredShops(shopDetails ? [shopDetails] : []);
    } else if (region) {
      setFilteredShops(
        allShops.filter((s) => s.region_id === parseInt(region)),
      );
    } else {
      setFilteredShops(allShops);
    }
  }, [loginType, loggedInShopId, region, allShops]);

  const formatIndianCurrency = (num) => {
    const n = parseFloat(num) || 0;
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getShopName = (id) =>
    allShops.find((s) => s.shop_details_id === id)?.shop_code || "-";

  const getRegionName = (id) =>
    regions.find((r) => r.region_id === id)?.city?.name || "-";

  const getTotalsByRegion = () => {
    const totals = {};
    formData.forEach((order) => {
      const regionName = order.region_name || "-";
      const amount = Number(order.order_overall_totall) || 0;
      totals[regionName] = (totals[regionName] || 0) + amount;
    });
    return totals;
  };

  const getTotalsByRegionProducts = () => {
    const stats = {};
    formData.forEach((order) => {
      const regionName = order.region_name || "-";
      const amount = Number(order.order_overall_totall) || 0;
      if (!stats[regionName]) {
        stats[regionName] = { total: 0, products: {} };
      }
      stats[regionName].total += amount;

      if (order.ordered_products && Array.isArray(order.ordered_products)) {
        order.ordered_products.forEach((product) => {
          let productDetails = product.product_details;
          if (Array.isArray(productDetails) && productDetails.length > 0) {
            productDetails = productDetails[0];
          }

          const productName = productDetails?.product_name || "Unknown Product";
          const productTotal = Number(product.product_total) || 0;
          const productQty = Number(product.product_quantity) || 0;

          if (!stats[regionName].products[productName]) {
            stats[regionName].products[productName] = {
              total: 0,
              quantity: 0,
            };
          }
          stats[regionName].products[productName].total += productTotal;
          stats[regionName].products[productName].quantity += productQty;
        });
      }
    });
    return stats;
  };

  const getTotalsByShop = () => {
    const stats = {};
    formData
      .filter((order) => {
        if (shop) {
          const effectiveShopId =
            order.delivery_mode === "Door Step Delivery"
              ? order.shop_orders?.shop_id
              : order.shop_id;
          return effectiveShopId === parseInt(shop);
        }
        return true;
      })
      .forEach((order) => {
        const shopName = order.shop_name || "-";
        const amount = Number(order.order_overall_totall) || 0;
        const deliveryMode = order.delivery_mode;

        if (!stats[shopName]) {
          stats[shopName] = {
            pickupTotal: 0,
            pickupCount: 0,
            doorstepTotal: 0,
            doorstepCount: 0,
            products: {},
          };
        }

        if (deliveryMode === "Pick Up") {
          stats[shopName].pickupTotal += amount;
          stats[shopName].pickupCount += 1;
        } else if (deliveryMode === "Door Step Delivery") {
          stats[shopName].doorstepTotal += amount;
          stats[shopName].doorstepCount += 1;
        }

        if (order.ordered_products && Array.isArray(order.ordered_products)) {
          order.ordered_products.forEach((product) => {
            let productDetails = product.product_details;

            if (Array.isArray(productDetails) && productDetails.length > 0) {
              productDetails = productDetails[0];
            }

            const productName =
              productDetails?.product_name || "Unknown Product";
            const productTotal = Number(product.product_total) || 0;
            const productQty = Number(product.product_quantity) || 0;

            if (!stats[shopName].products[productName]) {
              stats[shopName].products[productName] = {
                total: 0,
                quantity: 0,
              };
            }
            stats[shopName].products[productName].total += productTotal;
            stats[shopName].products[productName].quantity += productQty;
          });
        }
      });
    return stats;
  };

  const getStatsByDeliveryMode = () => {
    const stats = {};
    formData.forEach((order) => {
      const mode = order.delivery_mode || "Unknown";
      const amount = Number(order.order_overall_totall) || 0;
      if (!stats[mode]) {
        stats[mode] = { total: 0, count: 0 };
      }
      stats[mode].total += amount;
      stats[mode].count += 1;
    });
    return stats;
  };

  // ── Build a pivot-style "Report" summary sheet (added only for the "All Data" export) ──
  const addPivotSummarySheet = (workbook, orders, filters) => {
    const visibleOrders = (orders || []).filter((order) => {
      const selectedRegion = filters?.region ? String(filters.region) : "";
      const selectedShop = filters?.shop ? String(filters.shop) : "";
      const selectedOrderMethod =
        filters?.orderMethod && filters.orderMethod !== "ALL"
          ? filters.orderMethod
          : "";
      const selectedPlatform =
        filters?.platform && filters.platform !== "ALL"
          ? String(filters.platform).toUpperCase()
          : "";

      const effectiveRegionId = Number(
        order.delivery_mode === "Door Step Delivery"
          ? (order.shop_orders?.region_id ?? order.region_id ?? 0)
          : (order.region_id ?? 0),
      );
      const effectiveShopId = Number(
        order.delivery_mode === "Door Step Delivery"
          ? (order.shop_orders?.shop_id ?? order.shop_id ?? 0)
          : (order.shop_id ?? 0),
      );

      if (selectedRegion && effectiveRegionId !== Number(selectedRegion))
        return false;
      if (selectedShop && effectiveShopId !== Number(selectedShop))
        return false;
      if (
        selectedOrderMethod &&
        (order.delivery_mode || "") !== selectedOrderMethod
      )
        return false;
      if (
        selectedPlatform &&
        String(order.platform || "").toUpperCase() !== selectedPlatform
      )
        return false;

      // NOTE: no date re-filter here — `orders` (formData) is already
      // date-filtered server-side by loadOrders(). Re-filtering here by
      // order_delivered_at silently dropped undelivered/pending orders
      // (66 → 44 mismatch vs. the on-screen total).
      return true;
    });

    const sheet = workbook.addWorksheet("Report");

    const HEADER_FILL = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD9E1F2" },
    };
    const THIN = { style: "thin", color: { argb: "FF000000" } };
    const BOX_BORDER = { top: THIN, left: THIN, right: THIN, bottom: THIN };

    const fmtMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;

    const formatFilterDate = (value) => {
      if (!value) return "(All)";
      const match = value.match(/^\d{4}-\d{2}-\d{2}$/);
      if (!match) return value;
      const [year, month, day] = value.split("-");
      return `${day}/${month}/${year}`;
    };

    // order_delivered_at looks like "Monday 2026-08-10 12:00:00" — pull out the yyyy-mm-dd part
    const getDeliveredISO = (dateStr) => {
      if (!dateStr && dateStr !== 0) return null;

      const value = String(dateStr).trim();
      if (!value || value === "null" || value === "undefined") return null;

      const isoMatch = value.match(/(\d{4}-\d{2}-\d{2})/);
      if (isoMatch) return isoMatch[1];

      const dateMatch = value.match(/(\d{2}[/-]\d{2}[/-]\d{4})/);
      if (dateMatch) {
        const [d, m, y] = dateMatch[1].split(/[/-]/);
        return `${y}-${m}-${d}`;
      }

      const rawDate = new Date(value);
      if (!Number.isNaN(rawDate.getTime())) {
        const y = rawDate.getFullYear();
        const m = String(rawDate.getMonth() + 1).padStart(2, "0");
        const d = String(rawDate.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }

      const spacedParts = value.split(/\s+/);
      const isoFromParts = spacedParts.find((part) =>
        /^\d{4}-\d{2}-\d{2}$/.test(part),
      );
      if (isoFromParts) return isoFromParts;

      return null;
    };
    const isoToDMY = (iso) => {
      if (!iso) return "-";
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    };
    // Table 1 (Delivery Type → Region) should reflect ONLY the To Date, not
    // the whole from/to range — Tables 2 & 3 keep showing the full range.
    const toDateFilterISO = filters?.toDate || filters?.fromDate || null;
    const table1Orders = toDateFilterISO
      ? visibleOrders.filter(
          (o) => getDeliveredISO(o.order_delivered_at) === toDateFilterISO,
        )
      : visibleOrders;

    // ── Aggregate the already-filtered orders ──
    // ── Table 1 aggregation: To-Date-only orders ──
    const byModeRegion = {};
    table1Orders.forEach((o) => {
      const mode = o.delivery_mode || "-";
      const regionName = o.region_name || "-";
      const amt = Number(o.order_overall_totall) || 0;

      byModeRegion[mode] = byModeRegion[mode] || {
        count: 0,
        total: 0,
        regions: {},
      };
      byModeRegion[mode].count += 1;
      byModeRegion[mode].total += amt;
      byModeRegion[mode].regions[regionName] = byModeRegion[mode].regions[
        regionName
      ] || { count: 0, total: 0 };
      byModeRegion[mode].regions[regionName].count += 1;
      byModeRegion[mode].regions[regionName].total += amt;
    });

    const table1Count = table1Orders.length;
    const table1Total = table1Orders.reduce(
      (s, o) => s + (Number(o.order_overall_totall) || 0),
      0,
    );

    // ── Tables 2 & 3 aggregation: full filtered range (visibleOrders) ──
    const byRegion = {};
    const byRegionDate = {};

    visibleOrders.forEach((o) => {
      const regionName = o.region_name || "-";
      const amt = Number(o.order_overall_totall) || 0;
      const iso = getDeliveredISO(o.order_delivered_at);
      const dateLabel = iso ? isoToDMY(iso) : "-";

      byRegion[regionName] = byRegion[regionName] || { count: 0, total: 0 };
      byRegion[regionName].count += 1;
      byRegion[regionName].total += amt;

      byRegionDate[regionName] = byRegionDate[regionName] || {
        count: 0,
        total: 0,
        dates: {},
      };
      byRegionDate[regionName].count += 1;
      byRegionDate[regionName].total += amt;
      byRegionDate[regionName].dates[dateLabel] = byRegionDate[regionName]
        .dates[dateLabel] || {
        count: 0,
        total: 0,
        iso,
      };
      byRegionDate[regionName].dates[dateLabel].count += 1;
      byRegionDate[regionName].dates[dateLabel].total += amt;
    });

    const grandCount = visibleOrders.length;
    const grandTotal = visibleOrders.reduce(
      (s, o) => s + (Number(o.order_overall_totall) || 0),
      0,
    );

    const styleHeaderCells = (addrs) => {
      addrs.forEach((addr) => {
        const cell = sheet.getCell(addr);
        cell.font = { bold: true };
        cell.fill = HEADER_FILL;
        cell.border = BOX_BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
    };

    // Explicitly forces non-bold on detail rows so nothing inherits stray bold
    // from a previously-styled cell/row default sharing the same row number
    // (this worksheet has multiple side-by-side tables sharing row numbers).
    const clearFont = (row, cols) => {
      cols.forEach((c) => (row.getCell(c).font = { bold: false }));
    };

    // NOTE: showDropDown is intentionally OMITTED. In ExcelJS/OOXML, showDropDown: true
    // actually HIDES the in-cell dropdown arrow (the XML attribute name is inverted from
    // what it sounds like — it means "suppress dropdown"). Leaving it unset (or false)
    // is what makes the arrow appear.
    const validationSheet =
      workbook.getWorksheet("ReportLists") ||
      workbook.addWorksheet("ReportLists");
    validationSheet.state = "hidden";

    const addListValidation = (cellRef, formula) => {
      const cell = sheet.getCell(cellRef);
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        showInputMessage: true,
        formulae: [formula],
      };
    };

    const writeValidationList = (values, columnLetter) => {
      const startRow = 2;
      values.forEach((value, index) => {
        validationSheet.getCell(`${columnLetter}${startRow + index}`).value =
          value;
      });
      return `=ReportLists!$${columnLetter}$${startRow}:$${columnLetter}$${startRow + values.length - 1}`;
    };

    // sticky filter values for top row controls
    const platformValues = [
      "(All)",
      ...Array.from(
        new Set(visibleOrders.map((o) => o.platform || "(All)")),
      ).sort(),
    ];
    const deliveryTypeValues = [
      "(All)",
      ...Array.from(
        new Set(visibleOrders.map((o) => o.delivery_mode || "(All)")),
      ).sort(),
    ];
    const deliveredDateValues = Array.from(
      new Set(
        visibleOrders
          .map((o) => getDeliveredISO(o.order_delivered_at))
          .filter(Boolean)
          .map((iso) => isoToDMY(iso)),
      ),
    ).sort((a, b) => {
      const da = a.split("/").reverse().join("-");
      const db = b.split("/").reverse().join("-");
      return da.localeCompare(db, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    const selectedDateValue = filters?.toDate
      ? formatFilterDate(filters.toDate)
      : filters?.fromDate
        ? formatFilterDate(filters.fromDate)
        : deliveredDateValues.length > 0
          ? deliveredDateValues[deliveredDateValues.length - 1] // latest date
          : "(All)";

    const platformRange = writeValidationList(platformValues, "A");
    const deliveryTypeRange = writeValidationList(deliveryTypeValues, "B");
    const deliveredDateRange = writeValidationList(
      deliveredDateValues.length > 0 ? deliveredDateValues : ["(All)"],
      "C",
    );
const selectedDateValues =
  filters?.fromDate || filters?.toDate
    ? (() => {
        const fromValue = filters?.fromDate || filters?.toDate;
        const toValue = filters?.toDate || filters?.fromDate;
        if (fromValue && toValue && fromValue !== toValue) {
          return `${formatFilterDate(fromValue)} - ${formatFilterDate(toValue)}`;
        }
        return formatFilterDate(fromValue || toValue);
      })()
    : deliveredDateValues.length > 0
      ? deliveredDateValues[0]
      : "(All)";
    // ============ TABLE 1 : Delivery Type → Region (cols A-C) ============
    sheet.getCell("A1").value = "Delivered Date";
    sheet.getCell("A1").font = { bold: true };
    sheet.getCell("B1").value = selectedDateValue;
    addListValidation("B1", deliveredDateRange);

    sheet.getCell("A3").value = "Row Labels";
    sheet.getCell("B3").value = "Order Count";
    sheet.getCell("C3").value = "Actual Sales Amount  (₹)";
    styleHeaderCells(["A3", "B3", "C3"]);

    let r = 4;
    Object.keys(byModeRegion)
      .sort()
      .forEach((mode) => {
        const modeData = byModeRegion[mode];
        const modeRow = sheet.getRow(r);
        modeRow.getCell(1).value = mode;
        modeRow.getCell(2).value = modeData.count;
        modeRow.getCell(3).value = fmtMoney(modeData.total);
        [1, 2, 3].forEach((c) => {
          modeRow.getCell(c).font = { bold: true };
          modeRow.getCell(c).fill = HEADER_FILL;
          modeRow.getCell(c).border = BOX_BORDER;
        });
        r += 1;

        Object.keys(modeData.regions)
          .sort()
          .forEach((regionName) => {
            const rd = modeData.regions[regionName];
            const row = sheet.getRow(r);
            row.getCell(1).value = regionName;
            row.getCell(1).alignment = { indent: 1 };
            row.getCell(2).value = rd.count;
            row.getCell(3).value = fmtMoney(rd.total);
            row.outlineLevel = 1;
            clearFont(row, [1, 2, 3]);
            [1, 2, 3].forEach((c) => (row.getCell(c).border = BOX_BORDER));
            r += 1;
          });
      });

    const grand1 = sheet.getRow(r);
    grand1.getCell(1).value = "Grand Total";
    grand1.getCell(2).value = table1Count;
    grand1.getCell(3).value = fmtMoney(table1Total);
    [1, 2, 3].forEach((c) => {
      grand1.getCell(c).font = { bold: true };
      grand1.getCell(c).fill = HEADER_FILL;
      grand1.getCell(c).border = BOX_BORDER;
    });

    sheet.autoFilter = {
      from: { row: 3, column: 1 },
      to: { row: r, column: 3 },
    };

    sheet.getColumn(1).width = 22;
    sheet.getColumn(2).width = 14;
    sheet.getColumn(3).width = 22;

    // ============ TABLE 2 : Region totals (cols G-I) — plain styled range, no addTable ============
    sheet.getCell("G1").value = "Platform";
    sheet.getCell("H1").value =
      filters?.platform && filters.platform !== "ALL"
        ? filters.platform
        : "(All)";
    addListValidation("H1", platformRange);
    sheet.getCell("G2").value = "Delivered Date";
    sheet.getCell("H2").value = selectedDateValues;
    addListValidation("H2", deliveredDateRange);
    sheet.getCell("G3").value = "Delivery Type";
    sheet.getCell("H3").value =
      filters?.orderMethod && filters.orderMethod !== "ALL"
        ? filters.orderMethod
        : "(All)";
    addListValidation("H3", deliveryTypeRange);
    ["G1", "G2", "G3"].forEach((a) => (sheet.getCell(a).font = { bold: true }));

    sheet.getCell("G5").value = "Row Labels";
    sheet.getCell("H5").value = "Order Count";
    sheet.getCell("I5").value = "Actual Sales Amount  (₹)";
    styleHeaderCells(["G5", "H5", "I5"]);

    let r2 = 6;
    Object.keys(byRegion)
      .sort()
      .forEach((regionName) => {
        const rd = byRegion[regionName];
        const row = sheet.getRow(r2);
        row.getCell(7).value = regionName;
        row.getCell(8).value = rd.count;
        row.getCell(9).value = fmtMoney(rd.total);
        clearFont(row, [7, 8, 9]);
        [7, 8, 9].forEach((c) => (row.getCell(c).border = BOX_BORDER));
        r2 += 1;
      });
    const grand2 = sheet.getRow(r2);
    grand2.getCell(7).value = "Grand Total";
    grand2.getCell(8).value = grandCount;
    grand2.getCell(9).value = fmtMoney(grandTotal);
    [7, 8, 9].forEach((c) => {
      grand2.getCell(c).font = { bold: true };
      grand2.getCell(c).fill = HEADER_FILL;
      grand2.getCell(c).border = BOX_BORDER;
    });

    sheet.getColumn(7).width = 16;
    sheet.getColumn(8).width = 14;
    sheet.getColumn(9).width = 22;

    // ============ TABLE 3 : Region → Delivered Date (cols M-O) — plain styled range, no addTable ============
    sheet.getCell("M1").value = "Platform";
    sheet.getCell("N1").value = "(All)";
    addListValidation("N1", platformRange);
    sheet.getCell("M2").value = "Delivered Date";
    sheet.getCell("N2").value = selectedDateValues;
    addListValidation("N2", deliveredDateRange);
    sheet.getCell("M3").value = "Delivery Type";
    sheet.getCell("N3").value = "(All)";
    addListValidation("N3", deliveryTypeRange);
    ["M1", "M2", "M3"].forEach((a) => (sheet.getCell(a).font = { bold: true }));

    sheet.getCell("M5").value = "Row Labels";
    sheet.getCell("N5").value = "Order Count";
    sheet.getCell("O5").value = "Actual Sales Amount  (₹)";
    styleHeaderCells(["M5", "N5", "O5"]);

    let r3 = 6;
    Object.keys(byRegionDate)
      .sort()
      .forEach((regionName) => {
        const rd = byRegionDate[regionName];
        const row = sheet.getRow(r3);
        row.getCell(13).value = regionName;
        row.getCell(14).value = rd.count;
        row.getCell(15).value = fmtMoney(rd.total);
        [13, 14, 15].forEach((c) => {
          row.getCell(c).font = { bold: true };
          row.getCell(c).fill = HEADER_FILL;
          row.getCell(c).border = BOX_BORDER;
        });
        r3 += 1;

        const dateKeys = Object.keys(rd.dates).sort((a, b) => {
          const ia = rd.dates[a].iso || "";
          const ib = rd.dates[b].iso || "";
          return ia.localeCompare(ib);
        });
        dateKeys.forEach((dateLabel) => {
          const dd = rd.dates[dateLabel];
          const row2 = sheet.getRow(r3);
          row2.getCell(13).value = dateLabel;
          row2.getCell(13).alignment = { indent: 1 };
          row2.getCell(14).value = dd.count;
          row2.getCell(15).value = fmtMoney(dd.total);
          row2.outlineLevel = 1;
          clearFont(row2, [13, 14, 15]);
          [13, 14, 15].forEach((c) => (row2.getCell(c).border = BOX_BORDER));
          r3 += 1;
        });
      });
    const grand3 = sheet.getRow(r3);
    grand3.getCell(13).value = "Grand Total";
    grand3.getCell(14).value = grandCount;
    grand3.getCell(15).value = fmtMoney(grandTotal);
    [13, 14, 15].forEach((c) => {
      grand3.getCell(c).font = { bold: true };
      grand3.getCell(c).fill = HEADER_FILL;
      grand3.getCell(c).border = BOX_BORDER;
    });

    sheet.getColumn(13).width = 16;
    sheet.getColumn(14).width = 14;
    sheet.getColumn(15).width = 22;

    // ============ TABLE 4 : Region/Shop Top & Least Sellers (cols Q-X) — plain styled range, no addTable ============
    const byRegionShopProducts = {};
    visibleOrders.forEach((order) => {
      const regionName = order.region_name || "-";
      const shopName = order.shop_name || "-";
      const deliveredIso = getDeliveredISO(order.order_delivered_at);
      const dateLabel = deliveredIso ? isoToDMY(deliveredIso) : "-";

      if (!byRegionShopProducts[regionName]) {
        byRegionShopProducts[regionName] = {};
      }
      if (!byRegionShopProducts[regionName][shopName]) {
        byRegionShopProducts[regionName][shopName] = {};
      }
      if (!byRegionShopProducts[regionName][shopName][dateLabel]) {
        byRegionShopProducts[regionName][shopName][dateLabel] = {
          products: {},
        };
      }

      const products = Array.isArray(order.ordered_products)
        ? order.ordered_products
        : [];
      products.forEach((product) => {
        let productDetails = product.product_details;
        if (Array.isArray(productDetails) && productDetails.length > 0) {
          productDetails = productDetails[0];
        }
        const productName = productDetails?.product_name || "Unknown Product";
        const productTotal = Number(product.product_total) || 0;
        const productQty = Number(product.product_quantity) || 0;

        const productRow =
          byRegionShopProducts[regionName][shopName][dateLabel].products;
        if (!productRow[productName]) {
          productRow[productName] = { total: 0, quantity: 0 };
        }
        productRow[productName].total += productTotal;
        productRow[productName].quantity += productQty;
      });
    });

    if (Object.keys(byRegionShopProducts).length > 0) {
      const headerCells = [
        "Q1",
        "R1",
        "S1",
        "T1",
        "U1",
        "V1",
        "W1",
        "X1",
        "Y1",
      ];
      [
        "Region",
        "Date",
        "Shop",
        "Top Seller",
        "Top Qty",
        "Top Amount",
        "Least Seller",
        "Least Qty",
        "Least Amount",
      ].forEach((text, index) => {
        const cell = sheet.getCell(headerCells[index]);
        cell.value = text;
        cell.font = { bold: true };
        cell.fill = HEADER_FILL;
        cell.border = BOX_BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });

      let r4 = 2;
      let totalRowCount = 0;
      let totalTopQty = 0;
      let totalTopAmount = 0;
      let totalLeastQty = 0;
      let totalLeastAmount = 0;
      Object.keys(byRegionShopProducts)
        .sort()
        .forEach((regionName) => {
          Object.keys(byRegionShopProducts[regionName])
            .sort()
            .forEach((shopName) => {
              Object.keys(byRegionShopProducts[regionName][shopName])
                .sort((a, b) => (a < b ? -1 : 1))
                .forEach((dateLabel) => {
                  const products = Object.entries(
                    byRegionShopProducts[regionName][shopName][dateLabel]
                      .products,
                  );
                  if (!products.length) return;
                  const sortedProducts = products.sort(
                    (a, b) => b[1].total - a[1].total,
                  );
                  const topProduct = sortedProducts[0];
                  const leastProduct =
                    sortedProducts[sortedProducts.length - 1];

                  totalRowCount += 1;
                  totalTopQty += topProduct[1].quantity;
                  totalTopAmount += topProduct[1].total;
                  totalLeastQty += leastProduct[1].quantity;
                  totalLeastAmount += leastProduct[1].total;

                  const row = sheet.getRow(r4);
                  row.getCell(17).value = regionName; // Q
                  row.getCell(18).value = dateLabel; // R
                  row.getCell(19).value = shopName; // S
                  row.getCell(20).value = topProduct[0]; // T
                  row.getCell(21).value = topProduct[1].quantity; // U
                  row.getCell(22).value = fmtMoney(topProduct[1].total); // V
                  row.getCell(23).value = leastProduct[0]; // W
                  row.getCell(24).value = leastProduct[1].quantity; // X
                  row.getCell(25).value = fmtMoney(leastProduct[1].total); // Y
                  clearFont(row, [17, 18, 19, 20, 21, 22, 23, 24, 25]);
                  [17, 18, 19, 20, 21, 22, 23, 24, 25].forEach(
                    (c) => (row.getCell(c).border = BOX_BORDER),
                  );
                  r4 += 1;
                });
            });
        });

      const dataStartRow = 2;
      const dataEndRow = r4 - 1;

      const grand4 = sheet.getRow(r4);
      grand4.getCell(17).value = "Grand Total";
      grand4.getCell(19).value = {
        formula: `SUBTOTAL(103,S${dataStartRow}:S${dataEndRow})`,
      };
      grand4.getCell(21).value = {
        formula: `SUBTOTAL(109,U${dataStartRow}:U${dataEndRow})`,
      };
      grand4.getCell(22).value = {
        formula: `SUBTOTAL(109,V${dataStartRow}:V${dataEndRow})`,
      };
      grand4.getCell(24).value = {
        formula: `SUBTOTAL(109,X${dataStartRow}:X${dataEndRow})`,
      };
      grand4.getCell(25).value = {
        formula: `SUBTOTAL(109,Y${dataStartRow}:Y${dataEndRow})`,
      };
      [17, 18, 19, 20, 21, 22, 23, 24, 25].forEach((c) => {
        grand4.getCell(c).font = { bold: true };
        grand4.getCell(c).fill = HEADER_FILL;
        grand4.getCell(c).border = BOX_BORDER;
      });
      r4 += 1;

      sheet.autoFilter = {
        from: { row: 1, column: 17 },
        to: { row: r4 - 2, column: 25 },
      };

      sheet.getColumn(17).width = 18;
      sheet.getColumn(18).width = 14;
      sheet.getColumn(19).width = 22;
      sheet.getColumn(20).width = 26;
      sheet.getColumn(21).width = 12;
      sheet.getColumn(22).width = 14;
      sheet.getColumn(23).width = 26;
      sheet.getColumn(24).width = 12;
      sheet.getColumn(25).width = 14;
    }

    // Collapse controls sit above each group of detail rows, like a native pivot table
    sheet.properties.outlineProperties = {
      summaryBelow: false,
      summaryRight: false,
    };
  };

  const handleCustomDownload = async (mode = "all") => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Sales Report");

      const formatDate = (dateStr) => {
        if (!dateStr) return "_";
        const parts = dateStr.split(" ");
        const datePart = parts[1];
        if (!datePart) return "_";
        const [year, month, day] = datePart.split("-");
        const monthMap = {
          Jan: "01",
          Feb: "02",
          Mar: "03",
          Apr: "04",
          May: "05",
          Jun: "06",
          Jul: "07",
          Aug: "08",
          Sep: "09",
          Oct: "10",
          Nov: "11",
          Dec: "12",
        };
        return `${day}/${monthMap[month] || "00"}/${year}`;
      };

      const baseColumns = [
        { header: "Order ID", key: "order_id", width: 12 },
        { header: "Order Status", key: "order_status", width: 17 },
        { header: "Customer Name", key: "customer_name", width: 20 },
        { header: "Shop", key: "shop", width: 25 },
        { header: "Region", key: "region", width: 15 },
        { header: "Delivery Type", key: "delivery_type", width: 18 },
        { header: "Mobile", key: "mobile", width: 15 },
        { header: "Category Code", key: "cat_code", width: 18 },
        { header: "Product Code", key: "productname_sku", width: 30 },
        { header: "Product Name", key: "product_name", width: 45 },
        { header: "Variant", key: "variant", width: 15 },
        { header: "Product Qty", key: "product_qty", width: 12 },
        { header: "Product Total (₹)", key: "product_total", width: 15 },
        { header: "Week", key: "week_of_month", width: 10 },
        { header: "Order Day", key: "order_day", width: 12 },
        { header: "Order Date", key: "order_date", width: 15 },
        { header: "Payment Time", key: "order_time", width: 14 },
        { header: "Time Slot", key: "slot", width: 18 },
        { header: "Delivered Day", key: "delivery_day", width: 14 },
        { header: "Delivered Date", key: "delivery_date", width: 15 },
        { header: "Delivered Time", key: "delivery_time", width: 14 },
        { header: "Razorpay ID", key: "razorpay_id", width: 25 },
        { header: "Invoice No", key: "invoice_no", width: 16 },
        { header: "Sub Total (₹)", key: "amount_without_delivery", width: 15 },
        { header: "Delivery Fee (₹)", key: "delivery_fee", width: 15 },
        { header: "Platform", key: "platform", width: 12 },
        { header: "Total (₹)", key: "total", width: 12 },
      ];

      const addonColumns = [
        { header: "Order ID", key: "order_id", width: 12 },
        { header: "Order Status", key: "order_status", width: 17 },
        { header: "Customer Name", key: "customer_name", width: 20 },
        { header: "Shop", key: "shop", width: 25 },
        { header: "Region", key: "region", width: 15 },
        { header: "Delivery Type", key: "delivery_type", width: 18 },
        { header: "Mobile", key: "mobile", width: 15 },
        { header: "Addons Name", key: "addons_name", width: 35 },
        { header: "Addons Qty", key: "addons_qty", width: 12 },
        { header: "Addons Total (₹)", key: "addons_total", width: 15 },
        { header: "Week", key: "week_of_month", width: 10 },
        { header: "Order Day", key: "order_day", width: 12 },
        { header: "Order Date", key: "order_date", width: 15 },
        { header: "Payment Time", key: "order_time", width: 14 },
        { header: "Delivered Day", key: "delivery_day", width: 14 },
        { header: "Delivered Date", key: "delivery_date", width: 15 },
        { header: "Delivered Time", key: "delivery_time", width: 14 },
        { header: "Total (₹)", key: "total", width: 12 },
      ];

      const allColumns = [
        { header: "Order ID", key: "order_id", width: 12 },
        { header: "Order Status", key: "order_status", width: 17 },
        { header: "Customer Name", key: "customer_name", width: 20 },
        { header: "Shop", key: "shop", width: 25 },
        { header: "Region", key: "region", width: 15 },
        { header: "Delivery Type", key: "delivery_type", width: 18 },
        { header: "Mobile", key: "mobile", width: 15 },
        { header: "Category Code", key: "cat_code", width: 18 },
        { header: "Product Code", key: "productname_sku", width: 30 },
        { header: "Product Name", key: "product_name", width: 45 },
        { header: "Variant", key: "variant", width: 15 },
        { header: "Product Qty", key: "product_qty", width: 12 },
        { header: "Product Total (₹)", key: "product_total", width: 15 },
        { header: "Addons Name", key: "addons_name", width: 35 },
        { header: "Addons Qty", key: "addons_qty", width: 12 },
        { header: "Addons Total (₹)", key: "addons_total", width: 15 },
        { header: "Week", key: "week_of_month", width: 10 },
        { header: "Order Day", key: "order_day", width: 12 },
        { header: "Order Date", key: "order_date", width: 15 },
        { header: "Payment Time", key: "order_time", width: 14 },
        { header: "Time Slot", key: "slot", width: 18 },
        { header: "Delivered Day", key: "delivery_day", width: 14 },
        { header: "Delivered Date", key: "delivery_date", width: 15 },
        { header: "Delivered Time", key: "delivery_time", width: 14 },
        { header: "Razorpay ID", key: "razorpay_id", width: 25 },
        { header: "Invoice No", key: "invoice_no", width: 16 },
        { header: "Sub Total (₹)", key: "amount_without_delivery", width: 15 },
        { header: "Delivery Fee (₹)", key: "delivery_fee", width: 15 },
        { header: "Total Qty", key: "total_qty", width: 12 },
        { header: "Platform", key: "platform", width: 12 },
        { header: "Total (₹)", key: "total", width: 12 },
      ];

      if (mode === "without_addons") worksheet.columns = baseColumns;
      else if (mode === "addons_only") worksheet.columns = addonColumns;
      else worksheet.columns = allColumns;

      const toNumber = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      };

      const applyRowAlignment = (excelRow) => {
        for (let col = 1; col <= worksheet.columns.length; col++) {
          const cell = excelRow.getCell(col);
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: false,
          };
          cell.numFmt = "@";
        }
      };

      formData.forEach((row) => {
        const commonFields = {
          order_id: row.order_id || "_",
          order_status: row.order_status || "_",
          customer_name: row.billing_name || "_",
          shop: row.shop_name || "_",
          region: row.region_name || "_",
          delivery_type: row.delivery_mode || "_",
          mobile: row.billing_mobile_number
            ? Number(String(row.billing_mobile_number).replace(/\D/g, ""))
            : 0 || "_",
          week_of_month: row.week_of_month || "_",
          order_date: formatDate(row.paid_at),
          order_day: row.paid_at ? row.paid_at.split(" ")[0] : "_",
          order_time: row.paid_at
            ? row.paid_at.split(" ").slice(2).join(" ")
            : "_",
          slot: row.slot_timing || "_",
          delivery_date: row.order_delivered_at
            ? formatDate(row.order_delivered_at)
            : "_",
          delivery_day: row.order_delivered_at
            ? row.order_delivered_at.split(" ")[0]
            : "_",
          delivery_time: row.order_delivered_at
            ? row.order_delivered_at.split(" ").slice(2).join(" ")
            : "_",
          razorpay_id: row.razorpay_payment_id || "_",
          invoice_no: row.invoice_no || "_",
          amount_without_delivery:
            toNumber(row.order_overall_totall) - toNumber(row.deliver_fee),
          delivery_fee: toNumber(row.deliver_fee) || "_",
          platform: row.platform || "_",
          total: toNumber(row.order_overall_totall) || "_",
          addons_name: row.addons_names || "_",
          addons_qty: toNumber(row.addons_qty) || "_",
          addons_total: toNumber(row.addons_total) || "_",
        };

        if (mode === "addons_only") {
          const addons = row.ordered_addons || [];
          if (addons.length === 0) {
            worksheet.addRow({
              ...commonFields,
              addons_name: "_",
              addons_qty: 0,
              addons_total: 0,
            });
          } else {
            addons.forEach((addon, idx) => {
              worksheet.addRow({
                order_id: idx === 0 ? commonFields.order_id : "_",
                order_status: idx === 0 ? commonFields.order_status : "_",
                customer_name: idx === 0 ? commonFields.customer_name : "_",
                shop: idx === 0 ? commonFields.shop : "_",
                region: idx === 0 ? commonFields.region : "_",
                delivery_type: idx === 0 ? commonFields.delivery_type : "_",
                mobile: idx === 0 ? commonFields.mobile : "_",
                addons_name: addon ? addon.product_name || "_" : "_",
                addons_qty: addon ? Number(addon.quantity || 0) : "_",
                addons_total: addon ? Number(addon.price || 0) : "_",
                week_of_month: idx === 0 ? commonFields.week_of_month : "_",
                order_day: idx === 0 ? commonFields.order_day : "_",
                order_date: idx === 0 ? commonFields.order_date : "_",
                order_time: idx === 0 ? commonFields.order_time : "_",
                delivery_day: idx === 0 ? commonFields.delivery_day : "_",
                delivery_date: idx === 0 ? commonFields.delivery_date : "_",
                delivery_time: idx === 0 ? commonFields.delivery_time : "_",
                total: idx === 0 ? commonFields.total : "_",
              });
              applyRowAlignment(worksheet.lastRow);
              if (idx === 0) {
                worksheet.lastRow.getCell("mobile").numFmt = "0";
              }
            });
          }
        } else if (mode === "without_addons") {
          const products = row.products_list || [];
          if (products.length === 0) {
            worksheet.addRow({
              ...commonFields,
              product_name: "_",
              productname_sku: "_",
              cat_code: "_",
              variant: "_",
              product_qty: 0,
              product_total: 0,
            });
          } else {
            products.forEach((product, idx) => {
              worksheet.addRow({
                order_id: idx === 0 ? commonFields.order_id : "_",
                order_status: idx === 0 ? commonFields.order_status : "_",
                customer_name: idx === 0 ? commonFields.customer_name : "_",
                shop: idx === 0 ? commonFields.shop : "_",
                region: idx === 0 ? commonFields.region : "_",
                delivery_type: idx === 0 ? commonFields.delivery_type : "_",
                mobile: idx === 0 ? commonFields.mobile : "_",
                cat_code: product ? product.cat_code_id || "_" : "_",
                productname_sku: product ? product.productname_sku || "_" : "_",
                product_name: product ? product.product_name || "_" : "_",
                variant: product ? product.variant || "_" : "_",
                product_qty: product ? product.product_qty : "_",
                product_total: product ? product.product_total : "_",
                week_of_month: idx === 0 ? commonFields.week_of_month : "_",
                order_day: idx === 0 ? commonFields.order_day : "_",
                order_date: idx === 0 ? commonFields.order_date : "_",
                order_time: idx === 0 ? commonFields.order_time : "_",
                slot: idx === 0 ? commonFields.slot : "_",
                delivery_day: idx === 0 ? commonFields.delivery_day : "_",
                delivery_date: idx === 0 ? commonFields.delivery_date : "_",
                delivery_time: idx === 0 ? commonFields.delivery_time : "_",
                razorpay_id: idx === 0 ? commonFields.razorpay_id : "_",
                invoice_no: idx === 0 ? commonFields.invoice_no : "_",
                amount_without_delivery:
                  idx === 0 ? commonFields.amount_without_delivery : "_",
                delivery_fee: idx === 0 ? commonFields.delivery_fee : "_",
                platform: idx === 0 ? commonFields.platform : "_",
                total: idx === 0 ? commonFields.total : "_",
              });
              applyRowAlignment(worksheet.lastRow);
              if (idx === 0) {
                worksheet.lastRow.getCell("mobile").numFmt = "0";
              }
            });
          }
        } else {
          const products = row.products_list || [];
          const addons = row.ordered_addons || [];
          const totalRows = Math.max(products.length || 1, addons.length, 1);

          for (let i = 0; i < totalRows; i++) {
            const product = products[i] || null;
            const addon = addons[i] || null;

            worksheet.addRow({
              order_id: i === 0 ? commonFields.order_id : "_",
              order_status: i === 0 ? commonFields.order_status : "_",
              customer_name: i === 0 ? commonFields.customer_name : "_",
              shop: i === 0 ? commonFields.shop : "_",
              region: i === 0 ? commonFields.region : "_",
              delivery_type: i === 0 ? commonFields.delivery_type : "_",
              mobile: i === 0 ? commonFields.mobile : "_",
              week_of_month: i === 0 ? commonFields.week_of_month : "_",
              order_day: i === 0 ? commonFields.order_day : "_",
              order_date: i === 0 ? commonFields.order_date : "_",
              order_time: i === 0 ? commonFields.order_time : "_",
              slot: i === 0 ? commonFields.slot : "_",
              delivery_day: i === 0 ? commonFields.delivery_day : "_",
              delivery_date: i === 0 ? commonFields.delivery_date : "_",
              delivery_time: i === 0 ? commonFields.delivery_time : "_",
              razorpay_id: i === 0 ? commonFields.razorpay_id : "_",
              invoice_no: i === 0 ? commonFields.invoice_no : "_",
              amount_without_delivery:
                i === 0 ? commonFields.amount_without_delivery : "_",
              delivery_fee: i === 0 ? commonFields.delivery_fee : "_",
              platform: i === 0 ? commonFields.platform : "_",
              total: i === 0 ? commonFields.total : "_",
              total_qty: i === 0 ? row.qty || 0 : "_",
              cat_code: product ? product.cat_code_id || "_" : "_",
              productname_sku: product ? product.productname_sku || "_" : "_",
              product_name: product
                ? `${product.cat_code_id || ""} ${product.product_name || ""} ${product.variant || ""}`.trim() ||
                  "_"
                : "_",
              variant: product ? product.variant || "_" : "_",
              product_qty: product ? product.product_qty || "_" : "_",
              product_total: product ? product.product_total || "_" : "_",
              addons_name: addon ? addon.product_name || "_" : "_",
              addons_qty: addon ? Number(addon.quantity || 0) : "_",
              addons_total: addon ? Number(addon.price || 0) : "_",
            });
            applyRowAlignment(worksheet.lastRow);
            if (i === 0) {
              worksheet.lastRow.getCell("mobile").numFmt = "0";
            }
          }
        }
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;
      for (let col = 1; col <= worksheet.columns.length; col++) {
        headerRow.getCell(col).border = {
          top: { style: "medium", color: { argb: "FF000000" } },
          left: { style: "medium", color: { argb: "FF000000" } },
          right: { style: "medium", color: { argb: "FF000000" } },
          bottom: { style: "medium", color: { argb: "FF000000" } },
        };
      }

      const totalCols = worksheet.columns.length;
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          for (let col = 1; col <= totalCols; col++) {
            const cell = row.getCell(col);
            cell.alignment = {
              horizontal: "center",
              vertical: "middle",
              wrapText: false,
            };
            cell.border = {
              left: { style: "thin", color: { argb: "FF000000" } },
              right: { style: "thin", color: { argb: "FF000000" } },
            };
          }
        }
      });

      let currentRow = 2;
      formData.forEach((row) => {
        const productsLen = (row.products_list || []).length || 1;
        const addonsLen = (row.ordered_addons || []).length;

        const productCount =
          mode === "addons_only"
            ? addonsLen || 1
            : mode === "all"
              ? Math.max(productsLen, addonsLen, 1)
              : productsLen;

        if (productCount > 1) {
          let mergeCols = [];

          if (mode === "without_addons") {
            mergeCols = [
              1, 2, 3, 4, 5, 6, 7, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24,
              25, 26, 27,
            ];
          } else if (mode === "all") {
            mergeCols = [
              1, 2, 3, 4, 5, 6, 7, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27,
              28, 29, 30, 31,
            ];
          } else if (mode === "addons_only") {
            mergeCols = [1, 2, 3, 4, 5, 6, 7, 11, 12, 13, 14, 15, 16, 17, 18];
          }

          mergeCols.forEach((colIndex) => {
            try {
              worksheet.mergeCells(
                currentRow,
                colIndex,
                currentRow + productCount - 1,
                colIndex,
              );
              const mergedCell = worksheet.getRow(currentRow).getCell(colIndex);
              mergedCell.alignment = {
                horizontal: "center",
                vertical: "middle",
                wrapText: false,
              };
            } catch (e) {}
          });
        }

        const borderRowIndex = currentRow + productCount - 1;
        for (let col = 1; col <= totalCols; col++) {
          const cell = worksheet.getRow(borderRowIndex).getCell(col);
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: false,
          };
          cell.border = {
            left: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "medium", color: { argb: "FF000000" } },
          };
        }

        currentRow += productCount;
      });

      // ── Add the pivot-style summary sheet only for the "All Data" export ──
      // ── Add the pivot-style summary sheet + a real native PivotTable (only for "All Data") ──
      let blob;

      if (mode === "all") {
        addPivotSummarySheet(workbook, formData, {
          fromDate,
          toDate,
          orderMethod,
          platform,
        });

        const pivotRecords = formData.map((o) => ({
          Region: o.region_name || "-",
          Shop: o.shop_name || "-",
          "Delivery Mode": o.delivery_mode || "-",
          Platform: o.platform || "-",
          Total: Number(o.order_overall_totall) || 0,
        }));

        blob = await buildWorkbookWithPivotSafe({
          workbook,
          dataSheetName: "PivotData",
          fields: [
            { name: "Region", type: "string" },
            { name: "Shop", type: "string" },
            { name: "Delivery Mode", type: "string" },
            { name: "Platform", type: "string" },
            { name: "Total", type: "number" },
          ],
          records: pivotRecords,
          pivotSheetName: "Pivot Table",
          rowField: "Region",
          colField: "Delivery Mode",
          pageFields: ["Platform"],
          valueField: "Total",
          valueAgg: "sum",
        });
      } else {
        const buffer = await workbook.xlsx.writeBuffer();
        blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const names = {
        without_addons: "Product_Report",
        addons_only: "Addon_Report",
        all: "All_Data",
      };
      link.download = `Sales_Report_${names[mode]}_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel file:", error);
      alert("Failed to generate Excel file. Please try again.");
    }
  };

  const isRegionDisabled = loginType === "shop";
  const isShopDisabled = loginType === "shop";

  const columns = [
    { name: "order_id", label: "Order ID" },
    { name: "week_of_month", label: "Week" },
    { name: "billing_name", label: "Customer Name" },
    { name: "shop_name", label: "Shop" },
    { name: "region_name", label: "Region" },
    { name: "delivery_mode", label: "Delivery Type" },
    { name: "order_status", label: "Order Status" },
    {
      name: "payment_details",
      label: "Payment",
      options: {
        filter: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          const razorpayId = formData[dataIndex]?.razorpay_payment_id || "N/A";
          const invoiceNo = formData[dataIndex]?.invoice_no || "N/A";
          const mobile = formData[dataIndex]?.billing_mobile_number || "N/A";

          const tooltipContent = (
            <div style={{ padding: "4px" }}>
              <div style={{ marginBottom: "4px" }}>
                <strong>Razorpay ID:</strong> {razorpayId}
              </div>
              <div style={{ marginBottom: "4px" }}>
                <strong>Invoice No:</strong> {invoiceNo}
              </div>
              <div style={{ marginBottom: "4px" }}>
                <strong>Mobile No:</strong> {mobile}
              </div>
            </div>
          );

          return (
            <Tooltip title={tooltipContent} arrow placement="top">
              <Payment
                color="primary"
                fontSize="small"
                style={{ cursor: "pointer" }}
              />
            </Tooltip>
          );
        },
      },
    },
    {
      name: "order_overall_totall",
      label: "Total",
      options: {
        customBodyRenderLite: (i) => {
          const amt = formData[i]?.order_overall_totall;
          return amt ? `₹${formatIndianCurrency(amt)}` : "-";
        },
      },
    },
  ];

  const options = {
    filter: false,
    search: true,
    selectableRows: "none",
    rowsPerPage: 10,
    count: totalCount,
    serverSide: false,
    print: false,
    viewColumns: false,
    download: true,
    onDownload: (buildHead, buildBody, columns, data) => {
      handleCustomDownload();
      return false;
    },
    customToolbar: () => (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => handleCustomDownload("without_addons")}
          sx={{ textTransform: "none", fontSize: "0.75rem", mr: 1 }}
        >
          Product Report
        </Button>
        <Button
          variant="outlined"
          color="warning"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => handleCustomDownload("addons_only")}
          sx={{ textTransform: "none", fontSize: "0.75rem" }}
        >
          Addon Report
        </Button>
      </>
    ),
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No records found",
      },
    },
  };

  const deliveryStats = getStatsByDeliveryMode();
  const shopStats = getTotalsByShop();

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
            }}
          >
            <h4>Sales Report</h4>
            <Box sx={{ display: "flex", gap: 1 }}>
              {fromDate && <Chip label={`From: ${fromDate}`} size="small" />}
              {toDate && <Chip label={`To: ${toDate}`} size="small" />}
              {totalCount > 0 && (
                <Chip
                  label={`${totalCount} orders`}
                  color="success"
                  size="small"
                />
              )}
            </Box>
          </Box>

          {/* ── FILTER ROW — only mic button + status label added at the end ── */}
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Delivery Type</InputLabel>
                <Select
                  value={orderMethod}
                  label="Delivery Type"
                  onChange={(e) => setOrderMethod(e.target.value)}
                >
                  {orderMethods.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="small" disabled={isRegionDisabled}>
                <InputLabel>ALL Region</InputLabel>
                <Select
                  value={region}
                  label="Region"
                  onChange={(e) => {
                    setRegion(e.target.value);
                    if (loginType !== "shop") {
                      setShop("");
                    }
                  }}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {filteredRegions?.map((r) => (
                    <MenuItem key={r.region_id} value={r.region_id}>
                      {r.city?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1.8}>
              <FormControl fullWidth size="small" disabled={isShopDisabled}>
                <InputLabel>ALL Shop</InputLabel>
                <Select
                  value={shop}
                  label="Shop"
                  onChange={(e) => setShop(e.target.value)}
                >
                  <MenuItem value="">All Shops</MenuItem>
                  {filteredShops.map((s) => (
                    <MenuItem key={s.shop_details_id} value={s.shop_details_id}>
                      {s.shop_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── MIC BUTTON (new) ── */}
            {/* <Grid item xs="auto">
              <Tooltip
                title={
                  !isSupported
                    ? "Voice not supported in this browser"
                    : listening
                      ? "Listening… click to stop"
                      : "Click to filter by voice"
                }
              >
                <span>
                  <IconButton
                    onClick={handleMicClick}
                    disabled={!isSupported}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: listening ? "#d32f2f" : "#1976d2",
                      color: "white",
                      animation: listening
                        ? "micPulse 1.4s ease-in-out infinite"
                        : "none",
                      "@keyframes micPulse": {
                        "0%,100%": {
                          boxShadow: "0 0 0 3px rgba(211,47,47,0.3)",
                        },
                        "50%": { boxShadow: "0 0 0 7px rgba(211,47,47,0.05)" },
                      },
                      "&:hover": { bgcolor: listening ? "#b71c1c" : "#1565c0" },
                      "&.Mui-disabled": {
                        bgcolor: "#e0e0e0",
                        color: "#9e9e9e",
                      },
                    }}
                  >
                    {listening ? <MicIcon /> : <MicOffIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Grid> */}

            {/* ── VOICE STATUS LABEL (new) ── */}
            {/* {(listening || voiceStatus) && (
              <Grid item xs="auto">
                <Typography
                  variant="caption"
                  color={listening ? "error" : "text.secondary"}
                  sx={{ fontStyle: "italic" }}
                >
                  {listening ? "🎙 Listening…" : `Last: ${voiceStatus}`}
                </Typography>
              </Grid>
            )} */}
          </Grid>

          {/* ── VOICE HINT BAR (updated with month + relative-date + region/shop examples) ── */}
          {/* <Box
            sx={{
              mb: 2,
              p: 1,
              bgcolor: "#f0f4ff",
              borderRadius: 1,
              border: "1px solid #c5cae9",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              <strong>🎤 Voice:</strong> "pick up" · "doorstep" · "all delivery"
              · "march" or "march 2025" · "this month" · "last week" · "today" ·
              "from date 15 june 2025" · "to date today" · "region chennai" ·
              "shop Uthangudi" · "platform android" · "clear filters"
            </Typography>
          </Box> */}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <MUIDataTable
                data={formData}
                columns={columns}
                options={options}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {loginType !== "shop" && (
                  <Card sx={{ bgcolor: "#1976d2", color: "white" }}>
                    <CardContent>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.9, mb: 0.5 }}
                      >
                        Total Sales
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        ₹
                        {formatIndianCurrency(
                          summaryData?.total_sales_amount || 0,
                        )}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {summaryData?.total_delivered || 0} delivered
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.8, color: "#ffcdd2" }}
                        >
                          {summaryData?.total_cancelled || 0} cancelled
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {loginType !== "shop" && (
                  <Card>
                    <CardContent>
                      <Typography
                        variant="subtitle2"
                        sx={(theme) => ({
                          fontWeight: 600,
                          mb: 2,
                          color: theme.palette.mode === "dark" ? "#aaaaaa" : "#666",
                        })}
                      >
                        By Delivery Mode{" "}
                        {region && `- ${getRegionName(parseInt(region))}`}
                      </Typography>

                      <Box
                        sx={(theme) => ({
                          p: 1.5,
                          mb: 1.5,
                          bgcolor:
                            theme.palette.mode === "dark" ? "#16281c" : "#e8f5e9",
                          borderRadius: 1,
                          borderLeft: "4px solid #2e7d32",
                        })}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Pick Up
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={(theme) => ({
                            fontWeight: 600,
                            color:
                              theme.palette.mode === "dark" ? "#81c784" : "#2e7d32",
                          })}
                        >
                          ₹
                          {formatIndianCurrency(
                            deliveryStats["Pick Up"]?.total || 0,
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {deliveryStats["Pick Up"]?.count || 0} orders
                        </Typography>
                      </Box>

                      <Box
                        sx={(theme) => ({
                          p: 1.5,
                          bgcolor:
                            theme.palette.mode === "dark" ? "#2d2000" : "#fff3e0",
                          borderRadius: 1,
                          borderLeft: "4px solid #ed6c02",
                        })}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Door Step Delivery
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={(theme) => ({
                            fontWeight: 600,
                            color:
                              theme.palette.mode === "dark" ? "#ffb74d" : "#ed6c02",
                          })}
                        >
                          ₹
                          {formatIndianCurrency(
                            deliveryStats["Door Step Delivery"]?.total || 0,
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {deliveryStats["Door Step Delivery"]?.count || 0}{" "}
                          orders
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent>
                    <Typography
                      variant="subtitle2"
                      sx={(theme) => ({
                        fontWeight: 600,
                        mb: 1,
                        color: theme.palette.mode === "dark" ? "#aaaaaa" : "#666",
                      })}
                    >
                      {region ? "By Shop" : "By Region"}
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: loginType === "shop" ? 450 : 350,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {region
                        ? Object.entries(shopStats)
                            .filter(([shopName]) => shopName !== "-")
                            .map(([shopName, stats]) => {
                              const shopTotal =
                                stats.pickupTotal + stats.doorstepTotal;
                              const shopTotalCount =
                                stats.pickupCount + stats.doorstepCount;
                              const productEntries = Object.entries(
                                stats.products || {},
                              );
                              const sortedProducts = productEntries.sort(
                                (a, b) => b[1].total - a[1].total,
                              );
                              const topProduct = sortedProducts[0];
                              const leastProduct =
                                sortedProducts[sortedProducts.length - 1];

                              return (
                                <Box
                                  key={shopName}
                                  sx={(theme) => ({
                                    p: 1.5,
                                    bgcolor:
                                      theme.palette.mode === "dark"
                                        ? "#2a2a2a"
                                        : "#f5f5f5",
                                    borderRadius: 1,
                                    borderLeft: "4px solid #1565c0",
                                    mb: 1,
                                  })}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {shopName}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontWeight: 600,
                                        color: "text.secondary",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.6,
                                      }}
                                    >
                                      Total
                                    </Typography>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                      gap: 2,
                                      mb: 1,
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mb: stats.doorstepCount > 0 ? 0.5 : 0,
                                        }}
                                      >
                                        <StorefrontIcon
                                          sx={(theme) => ({
                                            fontSize: 18,
                                            color:
                                              theme.palette.mode === "dark"
                                                ? "#81c784"
                                                : "#2e7d32",
                                          })}
                                        />
                                        <Box>
                                          <Typography
                                            variant="body2"
                                            sx={(theme) => ({
                                              fontWeight: 600,
                                              color:
                                                theme.palette.mode === "dark"
                                                  ? "#81c784"
                                                  : "#2e7d32",
                                            })}
                                          >
                                            ₹
                                            {formatIndianCurrency(
                                              stats.pickupTotal,
                                            )}
                                          </Typography>
                                          <Box
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: "0.7rem" }}
                                          >
                                            {stats.pickupCount} orders
                                          </Box>
                                        </Box>
                                      </Box>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                      >
                                        <LocalShippingIcon
                                          sx={(theme) => ({
                                            fontSize: 18,
                                            color:
                                              theme.palette.mode === "dark"
                                                ? "#ffb74d"
                                                : "#ed6c02",
                                          })}
                                        />
                                        <Box>
                                          <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                          >
                                            ₹
                                            {formatIndianCurrency(
                                              stats.doorstepTotal,
                                            )}
                                          </Typography>
                                          <Box
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: "0.7rem" }}
                                          >
                                            {stats.doorstepCount} orders
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>

                                    <Box sx={{ textAlign: "right" }}>
                                      <Typography
                                        variant="h6"
                                        sx={(theme) => ({
                                          fontWeight: 600,
                                          color:
                                            theme.palette.mode === "dark"
                                              ? "#90caf9"
                                              : "#1565c0",
                                          lineHeight: 1.2,
                                        })}
                                      >
                                        ₹{formatIndianCurrency(shopTotal)}
                                      </Typography>
                                      <Box
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ fontSize: "0.7rem" }}
                                      >
                                        {shopTotalCount} orders
                                      </Box>
                                    </Box>
                                  </Box>

                                  {shop && productEntries.length > 0 && (
                                      <Box
                                        sx={(theme) => ({
                                          mt: 1,
                                          pt: 1.5,
                                          borderTop: `1px solid ${theme.palette.divider}`,
                                        })}
                                      >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          display: "block",
                                          mb: 1,
                                        }}
                                      >
                                        Product Performance
                                      </Typography>

                                      {topProduct && (
                                          <Card
                                            variant="outlined"
                                            sx={(theme) => ({
                                              mb: 1,
                                              bgcolor:
                                                theme.palette.mode === "dark"
                                                  ? "#16281c"
                                                  : "#e8f5e9",
                                              borderColor:
                                                theme.palette.mode === "dark"
                                                  ? "#2e5b34"
                                                  : "#c8e6c9",
                                            })}
                                          >
                                          <CardContent
                                            sx={{
                                              p: 1,
                                              "&:last-child": { pb: 1 },
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <EmojiEventsOutlinedIcon
                                                sx={{ fontSize: 18 }}
                                              />
                                              <Box sx={{ flex: 1 }}>
                                                <Typography
                                                  variant="body2"
                                                  sx={(theme) => ({
                                                    fontWeight: 600,
                                                    color:
                                                      theme.palette.mode === "dark"
                                                        ? "#aaaaaa"
                                                        : "#666",
                                                  })}
                                                >
                                                  Top Seller
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    fontWeight: 500,
                                                    fontSize: "0.8rem",
                                                  }}
                                                >
                                                  {topProduct[0]}
                                                </Typography>
                                                <Box
                                                  variant="caption"
                                                  color="text.secondary"
                                                  sx={{
                                                    fontSize: "0.7rem",
                                                    opacity: 0.6,
                                                    letterSpacing: 0.2,
                                                  }}
                                                >
                                                  Qty: {topProduct[1].quantity}
                                                </Box>
                                              </Box>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 600,
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                ₹
                                                {formatIndianCurrency(
                                                  topProduct[1].total,
                                                )}
                                              </Typography>
                                            </Box>
                                          </CardContent>
                                        </Card>
                                      )}

                                      {leastProduct &&
                                        sortedProducts.length > 1 && (
                                          <Card
                                            variant="outlined"
                                            sx={(theme) => ({
                                              bgcolor:
                                                theme.palette.mode === "dark"
                                                  ? "#2d2000"
                                                  : "#fff3e0",
                                              borderColor:
                                                theme.palette.mode === "dark"
                                                  ? "#6b4a00"
                                                  : "#ffe0b2",
                                            })}
                                          >
                                            <CardContent
                                              sx={{
                                                p: 1,
                                                "&:last-child": { pb: 1 },
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <TrendingDownIcon
                                                  sx={{ fontSize: 18 }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                  <Typography
                                                    variant="body2"
                                                    sx={(theme) => ({
                                                      fontWeight: 600,
                                                      color:
                                                        theme.palette.mode ===
                                                        "dark"
                                                          ? "#aaaaaa"
                                                          : "#666",
                                                    })}
                                                  >
                                                    Least Seller
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{
                                                      fontWeight: 500,
                                                      fontSize: "0.8rem",
                                                    }}
                                                  >
                                                    {leastProduct[0]}
                                                  </Typography>
                                                  <Box
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      opacity: 0.6,
                                                      letterSpacing: 0.2,
                                                    }}
                                                  >
                                                    Qty:{" "}
                                                    {leastProduct[1].quantity}
                                                  </Box>
                                                </Box>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontWeight: 600,
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  ₹
                                                  {formatIndianCurrency(
                                                    leastProduct[1].total,
                                                  )}
                                                </Typography>
                                              </Box>
                                            </CardContent>
                                          </Card>
                                        )}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })
                        : Object.entries(getTotalsByRegion()).map(
                            ([regionName, total]) => (
                              <Box
                                key={regionName}
                                sx={(theme) => ({
                                  display: "flex",
                                  justifyContent: "space-between",
                                  p: 1,
                                  bgcolor:
                                    theme.palette.mode === "dark"
                                      ? "#2a2a2a"
                                      : "#f5f5f5",
                                  borderRadius: 0.5,
                                })}
                              >
                                <Typography variant="body2">
                                  {regionName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={(theme) => ({
                                    fontWeight: 600,
                                    color:
                                      theme.palette.mode === "dark"
                                        ? "#90caf9"
                                        : "#1565c0",
                                  })}
                                >
                                  ₹{formatIndianCurrency(total)}
                                </Typography>
                              </Box>
                            ),
                          )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>

          {/* ── SNACKBAR (new) ── */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </>
  );
}

export default SalesReport;
